import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import {
  ArrowRight, MapPin, Compass, Activity, Network, Cable,
  Zap, Sparkles, Mountain
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

/**
 * Bulgaria / Sofia — "Balkan crossroads" page.
 *
 * Visual identity intentionally different from every other location page:
 * - Editorial dark hero with a wide horizontal "ping atlas" strip
 * - Asymmetric two-column scroll story (no centered card-grid)
 * - Palette: deep emerald + slate ink + warm amber accent
 * - Bulgarian folk geometry hinted by chevron dividers
 *
 * All page text lives inline as { en, ru } so we don't bloat
 * translations.js (only SEO meta is wired through t()).
 */

const COPY = {
  en: {
    badge: 'Sofia, Bulgaria — gateway between continents',
    h1Top: 'VPS in Bulgaria',
    h1Sub: 'Where East meets West',
    h1Bot: 'and your packets find their shortest path',
    lead: "Bulgaria is the quiet pivot of the Balkan internet — the routing point where Frankfurt, Istanbul, Athens and Bucharest converge in milliseconds. We put your VPS in the middle of all of them, in a Sofia carrier-neutral facility wired into BIX.BG, the country's main internet exchange.",
    cta: 'Deploy in Sofia',
    altCta: 'See plans',
    pingAtlasTitle: 'Ping atlas — average round-trip from Sofia',
    pingAtlasNote: 'Real numbers from our looking-glass — no carefully picked single hops.',
    routes: [
      { city: 'Bucharest',  ms: '8 ms',  hint: 'Local NREN peering' },
      { city: 'Istanbul',   ms: '14 ms', hint: 'Black-Sea backbone' },
      { city: 'Belgrade',   ms: '12 ms', hint: 'Single-AS path' },
      { city: 'Athens',     ms: '21 ms', hint: 'Submarine via TGN' },
      { city: 'Vienna',     ms: '24 ms', hint: 'BIX.BG → DE-CIX' },
      { city: 'Frankfurt',  ms: '30 ms', hint: 'DE-CIX peering' },
      { city: 'Warsaw',     ms: '35 ms', hint: 'Diverse fibre' },
      { city: 'Moscow',     ms: '46 ms', hint: 'Eastern reach' },
    ],
    storyTitle: 'Why our customers picked Sofia',
    storyKicker: 'Three things that the brochure of the average western European DC quietly omits.',
    chapters: [
      {
        n: '01',
        title: 'Latency you can write SLOs against',
        body: "Our Sofia rack is a single AS-hop away from BIX.BG, which connects domestic and trans-Balkan networks without the long detour through Frankfurt. For a customer base in Romania, Greece, Turkey, Serbia or North Macedonia, that one routing decision is usually the difference between 'snappy' and 'fine, I guess'."
      },
      {
        n: '02',
        title: 'Real EU jurisdiction without German prices',
        body: "Bulgaria has been an EU member since 2007 — full GDPR, full data sovereignty, full e-invoicing — at a cost basis that lets us price 1 GB of RAM at the same level as our cheapest German plan. You're not trading legal certainty for a discount; you're trading geography for it."
      },
      {
        n: '03',
        title: 'A diversification that actually diversifies',
        body: "If your primary VPS already lives in Frankfurt, Amsterdam or Helsinki, adding Sofia gives you a node on a different optical path, behind a different transit mix, in a different power grid. That's what redundancy looks like — not 'two regions of AWS that go down at the same time'."
      }
    ],
    figures: { uptime: '99.95%', port: '1 Gbps', ipv6: 'Native', power: 'A+B feeds' },
    figuresLabels: { uptime: 'Monthly SLA', port: 'Per VPS', ipv6: 'IPv6 stack', power: 'Redundant PSU' },
    quoteAttribution: 'Sofia → BIX.BG → 200+ AS peers, including DE-CIX',
    pricingTitle: 'VPS pricing in Bulgaria',
    pricingSub: 'Same plans, same pricing as the rest of our European fleet — pick one and we will spin it up in roughly 60 seconds.',
    finalKicker: 'A Bulgarian VPS where the routes already meet.',
    finalCta: 'Spin up your Bulgarian VPS',
  },
  ru: {
    badge: 'София, Болгария — перекрёсток континентов',
    h1Top: 'VPS в Болгарии',
    h1Sub: 'Где Восток встречается с Западом',
    h1Bot: 'и пакеты находят свой кратчайший путь',
    lead: 'Болгария — тихая ось балканского интернета: точка маршрутизации, где Франкфурт, Стамбул, Афины и Бухарест сходятся за единицы миллисекунд. Мы ставим ваш VPS в самом центре всего этого — в Софии, в carrier-neutral ЦОДе, подключённом к BIX.BG, главной точке обмена трафиком в стране.',
    cta: 'Развернуть в Софии',
    altCta: 'К тарифам',
    pingAtlasTitle: 'Атлас пингов — среднее RTT из Софии',
    pingAtlasNote: 'Реальные значения с нашего looking-glass — никакого аккуратно выбранного «лучшего хопа».',
    routes: [
      { city: 'Бухарест',  ms: '8 мс',  hint: 'Локальный NREN-пиринг' },
      { city: 'Стамбул',   ms: '14 мс', hint: 'Магистраль Чёрного моря' },
      { city: 'Белград',   ms: '12 мс', hint: 'Один AS-хоп' },
      { city: 'Афины',     ms: '21 мс', hint: 'Подводный кабель через TGN' },
      { city: 'Вена',      ms: '24 мс', hint: 'BIX.BG → DE-CIX' },
      { city: 'Франкфурт', ms: '30 мс', hint: 'Прямой пиринг DE-CIX' },
      { city: 'Варшава',   ms: '35 мс', hint: 'Резервированный оптикой' },
      { city: 'Москва',    ms: '46 мс', hint: 'Восточный охват' },
    ],
    storyTitle: 'Почему клиенты выбирают Софию',
    storyKicker: 'Три вещи, которые в брошюрах средних западноевропейских ЦОДов обычно тихо опускают.',
    chapters: [
      {
        n: '01',
        title: 'Задержки, под которые можно писать SLO',
        body: 'Наш стек в Софии — один AS-хоп до BIX.BG, который связывает локальные и трансбалканские сети без долгого крюка через Франкфурт. Для аудитории в Румынии, Греции, Турции, Сербии или Северной Македонии именно это маршрутное решение чаще всего отделяет «отзывчиво» от «ну, в принципе нормально».'
      },
      {
        n: '02',
        title: 'Настоящая юрисдикция ЕС без немецкого ценника',
        body: 'Болгария — член ЕС с 2007 года: полноценный GDPR, полноценный data sovereignty, полноценный e-invoicing — но при себестоимости, которая позволяет продавать 1 GB RAM по цене самого дешёвого нашего немецкого тарифа. Вы не меняете правовую определённость на скидку — вы меняете на неё географию.'
      },
      {
        n: '03',
        title: 'Резервирование, которое реально резервирует',
        body: 'Если основной VPS уже стоит во Франкфурте, Амстердаме или Хельсинки — добавление Софии даёт ноду на другом оптическом пути, за другим миксом транзитов, в другой энергосети. Вот что такое настоящая избыточность, а не «два региона одного облака, которые падают одновременно».'
      }
    ],
    figures: { uptime: '99.95%', port: '1 Гбит/с', ipv6: 'Нативный', power: 'A+B питание' },
    figuresLabels: { uptime: 'SLA по uptime', port: 'На каждый VPS', ipv6: 'IPv6-стек', power: 'Резервные БП' },
    quoteAttribution: 'София → BIX.BG → 200+ AS-пиров, включая DE-CIX',
    pricingTitle: 'Цены на VPS в Болгарии',
    pricingSub: 'Те же планы и те же цены, что и для остальных европейских локаций — выбирайте, и развернём примерно за 60 секунд.',
    finalKicker: 'Болгарский VPS там, где маршруты уже сходятся.',
    finalCta: 'Поднять болгарский VPS',
  }
};

export const BulgariaPage = () => {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;
  const [revealed, setRevealed] = useState(false);
  const atlasRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setRevealed(true); },
      { threshold: 0.25 }
    );
    if (atlasRef.current) obs.observe(atlasRef.current);
    return () => obs.disconnect();
  }, []);

  const scrollPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="bulgaria" />
      <Header />

      {/* === HERO — editorial dark, no centered card === */}
      <section className="relative bg-[#0c1f1a] text-white overflow-hidden">
        {/* Subtle Bulgarian-folk-inspired chevron pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: 'repeating-linear-gradient(135deg, #fbbf24 0 1px, transparent 1px 18px), repeating-linear-gradient(45deg, #34d399 0 1px, transparent 1px 18px)' }} />
        <div className="absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-[28rem] h-[28rem] rounded-full bg-amber-500/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] bg-white/5 border border-white/15 text-emerald-200 mb-10">
            <Compass className="w-3.5 h-3.5" />
            {c.badge}
          </div>

          {/* Asymmetric headline: top line large, bottom line italic-display */}
          <h1 className="font-black tracking-tight max-w-5xl">
            <span className="block text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">{c.h1Top}</span>
            <span className="block mt-4 text-3xl sm:text-4xl lg:text-5xl text-amber-300 italic font-light leading-[1.05]">
              {c.h1Sub}
            </span>
            <span className="block mt-2 text-xl sm:text-2xl lg:text-3xl text-emerald-100/85 font-light leading-snug max-w-3xl">
              {c.h1Bot}
            </span>
          </h1>

          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <p className="lg:col-span-7 text-lg sm:text-xl text-emerald-50/85 leading-relaxed max-w-2xl">
              {c.lead}
            </p>
            <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-3 lg:pt-2">
              <Button onClick={scrollPricing} className="bg-amber-400 hover:bg-amber-300 text-[#0c1f1a] font-bold px-8 py-6 text-lg rounded-md shadow-2xl shadow-amber-500/20">
                {c.cta} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <button onClick={scrollPricing} className="text-emerald-200/80 underline decoration-dotted underline-offset-4 hover:text-amber-300 text-sm">
                {c.altCta} ↓
              </button>
            </div>
          </div>
        </div>

        {/* Folk-chevron divider */}
        <div className="relative h-6 bg-[#0c1f1a]">
          <svg className="absolute bottom-0 w-full h-6 text-white" preserveAspectRatio="none" viewBox="0 0 100 6">
            <path d="M0 6 L4 0 L8 6 L12 0 L16 6 L20 0 L24 6 L28 0 L32 6 L36 0 L40 6 L44 0 L48 6 L52 0 L56 6 L60 0 L64 6 L68 0 L72 6 L76 0 L80 6 L84 0 L88 6 L92 0 L96 6 L100 0 L100 6 Z" fill="currentColor" />
          </svg>
        </div>
      </section>


      {/* === IMAGE STRIP — Sofia panorama + Vitosha mountain === */}
      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 relative rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-100 to-emerald-200 aspect-[16/9] shadow-lg">
              <img
                src="/images/locations/sofia-cathedral.jpg"
                alt="Russian church of Saint Nicholas in Sofia, Bulgaria"
                loading="lazy"
                width="1400" height="787"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="lg:col-span-2 relative rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-100 to-amber-100 aspect-[16/9] shadow-lg">
              <img
                src="/images/locations/sofia-vitosha.jpg"
                alt="Vitosha mountain rising over Sofia"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                loading="lazy"
                width="900" height="506"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
      {/* === PING ATLAS — wide horizontal scroll-snap === */}
      <section ref={atlasRef} className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 max-w-2xl flex items-center gap-3">
              <Activity className="w-7 h-7 text-emerald-700" />
              {c.pingAtlasTitle}
            </h2>
            <p className="text-sm text-slate-500 italic max-w-md">{c.pingAtlasNote}</p>
          </div>

          <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 overflow-x-auto pb-3">
            <div className="flex gap-3 min-w-min snap-x snap-mandatory">
              {c.routes.map((r, i) => (
                <div
                  key={i}
                  className={`snap-start shrink-0 w-56 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50/30 px-5 py-5 transition-all duration-700 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-center gap-2 text-emerald-700 text-xs uppercase tracking-wider mb-1">
                    <MapPin className="w-3.5 h-3.5" />
                    Sofia → {r.city}
                  </div>
                  <div className="text-4xl font-black text-slate-900 leading-none mt-1">{r.ms}</div>
                  <div className="text-xs text-slate-500 mt-3 border-t border-dashed border-slate-200 pt-3">{r.hint}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === STORY — three numbered chapters in asymmetric two-column === */}
      <section className="bg-[#0c1f1a] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <h2 className="lg:col-span-7 text-4xl sm:text-5xl font-black tracking-tight">{c.storyTitle}</h2>
            <p className="lg:col-span-5 text-emerald-100/80 text-lg lg:pt-3">{c.storyKicker}</p>
          </div>

          <div className="space-y-16">
            {c.chapters.map((ch, i) => (
              <article key={i} className="grid lg:grid-cols-12 gap-8 items-start border-t border-white/10 pt-10">
                <div className="lg:col-span-3">
                  <div className="text-7xl font-black text-amber-300 leading-none">{ch.n}</div>
                </div>
                <div className="lg:col-span-9">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-white">{ch.title}</h3>
                  <p className="text-emerald-50/85 text-lg leading-relaxed max-w-3xl">{ch.body}</p>
                </div>
              </article>
            ))}
          </div>

          {/* Figures strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-b border-white/10 divide-x divide-white/10">
            {Object.entries(c.figures).map(([k, v]) => (
              <div key={k} className="px-4 py-6 text-center">
                <div className="text-3xl font-black text-amber-300">{v}</div>
                <div className="text-xs uppercase tracking-wider text-emerald-100/60 mt-1">{c.figuresLabels[k]}</div>
                {k === 'port' && (
                  <Link
                    to={(language === 'ru' ? '/ru' : '') + '/wiki/shared-1gbps-port-explained'}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-300/80 hover:text-amber-200 transition-colors normal-case tracking-normal"
                  >
                    {language === 'ru' ? 'Что это значит?' : 'What does this mean?'}
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-xs uppercase tracking-[0.2em] text-emerald-200/60 mt-8 flex items-center justify-center gap-2">
            <Cable className="w-4 h-4" /> {c.quoteAttribution}
          </p>
        </div>
      </section>

      <RelatedLocations currentLocation="vps-bulgaria" />

      <div id="pricing">
        <PricingSection hideHeader={false} title={c.pricingTitle} subtitle={c.pricingSub} />
      </div>
      <PaymentMethodsSection transparentBg={false} />

      {/* === FINAL CTA — text-only, no card === */}
      <section className="bg-gradient-to-b from-white to-emerald-50/40 py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mountain className="w-10 h-10 text-emerald-700 mx-auto mb-6" />
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mb-10 leading-tight">
            {c.finalKicker}
          </h2>
          <Button onClick={scrollPricing} className="bg-[#0c1f1a] hover:bg-[#143028] text-white font-bold px-10 py-7 text-lg rounded-md">
            {c.finalCta} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-bulgaria" />
      <Footer />
    </div>
  );
};
